/**
 * @file Higher order component providing all necessary mutations
 * for Favorite Photos in Web Galleries.
 */

import { graphql } from 'react-apollo';
import compose from 'lodash.flowright';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { defaultDataIdFromObject } from 'apollo-cache-inmemory';

import {
  favoritePhotosCreate,
  favoritePhotosDelete,
} from '../../../graphql/mutations/photos';
import {
  AlbumFavoritePhotoIds,
  AlbumFolderFavoritePhotoIds,
  AlbumAccountFavoritePhotos,
  AlbumFolderAccountFavoritePhotos,
} from '../graphql/queries/favorites';
import { AccountForWebGalleries } from '../graphql/queries/account';
import { PhotoFavoriteDetails } from '../../../graphql/fragments/photo';
import { AlbumForMatches } from '../../pro/views/HierarchyItem/Matches/graphql/queries/album';

const filterFavoritePhotoById = photoIds => favoritePhoto => {
  if (Array.isArray(photoIds)) {
    return !photoIds.includes(favoritePhoto.photo.id);
  }

  return photoIds !== favoritePhoto.photo.id;
};

const mapStateToProps = ({ auth }) => ({
  authenticated: auth.authenticated,
});

const WithAccountId = graphql(AccountForWebGalleries, {
  props: ({ data }) => ({
    accountId: data?.me?.id,
  }),
  skip: ({ authenticated }) => !authenticated,
});

// Since HierarchyItems can either be an album OR an albumFolder, we need to rely
// on an actual, fetched entity. This means we'll try to fetch the album used for
// matches (which would've already been loaded by the main view) and use it for
// favoriting from the album level.
const withAlbumForMatches = graphql(AlbumForMatches, {
  options: ({ match }) => ({
    variables: {
      id: match.params.hierarchyItemId,
    },
    fetchPolicy: 'cache-only',
  }),
  props: ({ data: { album } }) => ({
    albumForMatches: album,
  }),
  skip: ({ match }) => !match.params.hierarchyItemId,
});

const withFavoritePhotosCreateMutation = graphql(favoritePhotosCreate, {
  props: ({ mutate, ownProps: { accountId, albumForMatches, match } }) => ({
    favoritePhotosCreate: (photoIds, albumId = undefined) =>
      mutate({
        variables: {
          input: {
            account_id: accountId,
            album_id: albumId ?? albumForMatches?.id ?? match.params.albumId,
            photo_ids: Array.isArray(photoIds) ? photoIds : [photoIds],
          },
        },
        optimisticResponse: (() => {
          const ids = Array.isArray(photoIds) ? photoIds : [photoIds];

          return {
            __typename: 'Mutation',
            favoritePhotosCreate: {
              __typename: 'FavoritePhotosCreatePayload',
              favorite_photos: ids.map(id => ({
                __typename: 'FavoritePhoto',
                id: Date().toString(),
                photo: {
                  __typename: 'Photo',
                  id,
                  is_favorite: true,
                },
              })),
            },
          };
        })(),
        update: (
          store,
          {
            data: {
              favoritePhotosCreate: { favorite_photos: favoritePhotos },
            },
          },
        ) => {
          try {
            favoritePhotos.forEach(favoritePhoto => {
              const { id } = favoritePhoto.photo;
              const favoritePhotoFragment = store.readFragment({
                id: defaultDataIdFromObject({
                  id,
                  __typename: 'Photo',
                }),
                fragment: PhotoFavoriteDetails,
              });

              favoritePhotoFragment.is_favorite = true;

              store.writeFragment({
                id: defaultDataIdFromObject({
                  id,
                  __typename: 'Photo',
                }),
                fragment: PhotoFavoriteDetails,
                data: favoritePhotoFragment,
              });
            });
          } catch (e) {
            //  Nothing to do, fragment might not be loaded
          }
        },
        refetchQueries: (() => {
          const queries = [];
          const albumQueryId =
            albumId ?? match.params.albumId ?? match?.params?.hierarchyItemId;
          const albumFolderQueryId =
            match?.params?.folderId ?? match?.params?.hierarchyItemId;

          if (albumQueryId) {
            queries.push({
              query: AlbumFavoritePhotoIds,
              variables: {
                id: albumQueryId,
                limit: 1,
              },
            });
          }

          if (albumFolderQueryId) {
            queries.push({
              query: AlbumFolderFavoritePhotoIds,
              variables: {
                id: albumFolderQueryId,
                limit: 1,
              },
            });
          }

          return queries;
        })(),
      }),
  }),
});

const withFavoritePhotosDeleteMutation = graphql(favoritePhotosDelete, {
  props: ({ mutate, ownProps: { match } }) => ({
    favoritePhotosDelete: photoIds =>
      mutate({
        variables: {
          input: {
            photo_ids: Array.isArray(photoIds) ? photoIds : [photoIds],
          },
        },
        optimisticResponse: {
          __typename: 'Mutation',
          favoritePhotosDelete: {
            __typename: 'FavoritePhotosDeletePayload',
            success: true,
          },
        },
        update: store => {
          const { albumId, hierarchyItemId, folderId } = match.params;

          if (folderId || hierarchyItemId) {
            // AlbumFolder view (Favorites page)
            try {
              const { albumFolder } = store.readQuery({
                query: AlbumFolderAccountFavoritePhotos,
                variables: {
                  id: folderId ?? hierarchyItemId,
                },
              });

              store.writeQuery({
                query: AlbumFolderAccountFavoritePhotos,
                variables: {
                  id: folderId ?? hierarchyItemId,
                },
                data: {
                  albumFolder: {
                    ...albumFolder,
                    account_favorite_photos: {
                      ...albumFolder.account_favorite_photos,
                      records: albumFolder.account_favorite_photos.records.filter(
                        filterFavoritePhotoById(photoIds),
                      ),
                    },
                  },
                },
              });
            } catch (error) {
              // Query is not in store, ignore errors
            }
          }

          if (albumId || hierarchyItemId) {
            // Album view (Favorites page)
            try {
              const { album } = store.readQuery({
                query: AlbumAccountFavoritePhotos,
                variables: {
                  id: albumId ?? hierarchyItemId,
                },
              });

              store.writeQuery({
                query: AlbumAccountFavoritePhotos,
                variables: {
                  id: albumId ?? hierarchyItemId,
                },
                data: {
                  album: {
                    ...album,
                    account_favorite_photos: {
                      ...album.account_favorite_photos,
                      total: album.account_favorite_photos.total - 1,
                      records: album.account_favorite_photos.records.filter(
                        filterFavoritePhotoById(photoIds),
                      ),
                    },
                  },
                },
              });
            } catch (error) {
              // Query is not in store, ignore errors
            }
          }

          try {
            if (Array.isArray(photoIds)) {
              photoIds.forEach(photoId => {
                const favoritePhotoFragment = store.readFragment({
                  id: defaultDataIdFromObject({
                    id: photoId,
                    __typename: 'Photo',
                  }),
                  fragment: PhotoFavoriteDetails,
                });

                favoritePhotoFragment.is_favorite = false;

                store.writeFragment({
                  id: defaultDataIdFromObject({
                    id: photoId,
                    __typename: 'Photo',
                  }),
                  fragment: PhotoFavoriteDetails,
                  data: favoritePhotoFragment,
                });
              });
            } else {
              const favoritePhotoFragment = store.readFragment({
                id: defaultDataIdFromObject({
                  id: photoIds,
                  __typename: 'Photo',
                }),
                fragment: PhotoFavoriteDetails,
              });

              favoritePhotoFragment.is_favorite = false;

              store.writeFragment({
                id: defaultDataIdFromObject({
                  id: photoIds,
                  __typename: 'Photo',
                }),
                fragment: PhotoFavoriteDetails,
                data: favoritePhotoFragment,
              });
            }
          } catch (e) {
            //  Nothing to do, fragment might not be loaded
          }
        },
        refetchQueries: (() => {
          const queries = [];
          const albumQueryId =
            match.params.albumId ?? match?.params?.hierarchyItemId;
          const albumFolderQueryId =
            match?.params?.folderId ?? match?.params?.hierarchyItemId;

          if (albumQueryId) {
            queries.push({
              query: AlbumFavoritePhotoIds,
              variables: {
                id: albumQueryId,
                limit: 1,
              },
            });
          }

          if (albumFolderQueryId) {
            queries.push({
              query: AlbumFolderFavoritePhotoIds,
              variables: {
                id: albumFolderQueryId,
                limit: 1,
              },
            });
          }

          return queries;
        })(),
      }),
  }),
});

export default compose(
  withRouter,
  connect(mapStateToProps),
  WithAccountId,
  withAlbumForMatches,
  withFavoritePhotosCreateMutation,
  withFavoritePhotosDeleteMutation,
);
